import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Event } from '@/lib/types';

interface EventStatistic {
  eventId: string;
  eventName: string;
  eventDate: string;
  location: string;
  totalViews: number;
  uniqueViews: number;
  clickThroughRate: number;
  lastUpdated: string;
}

interface OrganizerEventStatisticsProps {
  events: Event[];
}

const OrganizerEventStatistics: React.FC<OrganizerEventStatisticsProps> = ({ events }) => {
  const { user } = useAuth();
  const [statistics, setStatistics] = useState<EventStatistic[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadStatistics = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // If no events are available, show appropriate message
      if (events.length === 0) {
        setStatistics([]);
        setIsLoading(false);
        return;
      }

      // Filter events with valid IDs
      const eventsWithIds = events.filter(event => event.id);
      if (eventsWithIds.length === 0) {
        console.warn('No events with valid IDs found');
        setStatistics([]);
        setIsLoading(false);
        return;
      }

      const eventIds = eventsWithIds.map(event => event.code!);

      // Fetch analytics events for the organizer's events
      const { data: analyticsData, error } = await supabase
        .from('analytics_events')
        .select('*')
        .in('event_id', eventIds)
        .in('event_type', ['event_view', 'event_click']);

      if (error) {
        console.error('Error fetching analytics data:', error);
        return;
      }

      // Process the analytics data
      const eventStats: EventStatistic[] = eventsWithIds.map(event => {
        const eventAnalytics = analyticsData?.filter(a => a.event_id === event.code) || [];

        // Count views and clicks
        const views = eventAnalytics.filter(a => a.event_type === 'event_view');
        const clicks = eventAnalytics.filter(a => a.event_type === 'event_click');

        // Calculate unique views using session_id
        const uniqueSessionIds = new Set(views.map(v => v.session_id));
        const uniqueViews = uniqueSessionIds.size;

        const totalViews = views.length;
        const totalClicks = clicks.length;
        const clickThroughRate = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;

        // Get the most recent event timestamp
        const timestamps = eventAnalytics.map(a => new Date(a.timestamp));
        const lastUpdated = timestamps.length > 0
          ? new Date(Math.max(...timestamps.map(t => t.getTime()))).toISOString()
          : new Date().toISOString();

        return {
          eventId: event.id!,
          eventName: event.name,
          eventDate: event.dateStart,
          location: event.venue?.city || 'Online',
          totalViews,
          uniqueViews,
          clickThroughRate,
          lastUpdated,
        };
      });

      setStatistics(eventStats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = () => {
    const csvHeaders = ['Esemény neve', 'Dátum', 'Helyszín', 'Összes megtekintés', 'Egyedi megtekintések', 'Átkattintási arány (%)', 'Utoljára frissítve'];
    const csvData = statistics.map(stat => [
      stat.eventName,
      new Date(stat.eventDate).toLocaleDateString('hu-HU'),
      stat.location,
      stat.totalViews,
      stat.uniqueViews,
      stat.clickThroughRate.toFixed(2),
      new Date(stat.lastUpdated).toLocaleString('hu-HU')
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `esemeny_statisztikak_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  useEffect(() => {
    loadStatistics();
  }, [events, user]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Esemény statisztikák</CardTitle>
          <CardDescription>Eseményeid megtekintési és kattintási statisztikái</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Statisztikák betöltése...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Esemény statisztikák</CardTitle>
            <CardDescription>Eseményeid megtekintési és kattintási statisztikái</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadStatistics}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Frissítés
            </Button>
            {statistics.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                CSV export
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {statistics.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">Esemény neve</th>
                  <th scope="col" className="px-6 py-3">Dátum</th>
                  <th scope="col" className="px-6 py-3">Helyszín</th>
                  <th scope="col" className="px-6 py-3">Összes megtekintés</th>
                  <th scope="col" className="px-6 py-3">Egyedi megtekintések</th>
                  <th scope="col" className="px-6 py-3">Átkattintási arány (%)</th>
                  <th scope="col" className="px-6 py-3">Utoljára frissítve</th>
                </tr>
              </thead>
              <tbody>
                {statistics.map((stat) => (
                  <tr key={stat.eventId} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                      {stat.eventName}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(stat.eventDate).toLocaleDateString('hu-HU')}
                    </td>
                    <td className="px-6 py-4">
                      {stat.location}
                    </td>
                    <td className="px-6 py-4">
                      {stat.totalViews}
                    </td>
                    <td className="px-6 py-4">
                      {stat.uniqueViews}
                    </td>
                    <td className="px-6 py-4">
                      {stat.clickThroughRate.toFixed(2)}%
                    </td>
                    <td className="px-6 py-4">
                      {new Date(stat.lastUpdated).toLocaleString('hu-HU', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            {events.length === 0
              ? "Még nincsenek létrehozott események. Hozz létre eseményeket a statisztikák megtekintéséhez."
              : "Még nincsenek elérhető statisztikák az eseményeidhez."
            }
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizerEventStatistics;